import React, {createContext, useContext, useEffect, useState} from 'react'
import {io, Socket} from 'socket.io-client'
import {useAuth} from '../modules/auth'

interface SocketContextType {
  socket: Socket | null
  connected: boolean
}

const SocketContext = createContext<SocketContextType>({socket: null, connected: false})

export const useSocket = () => useContext(SocketContext)

export const SocketProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
  const {auth, currentUser} = useAuth()
  const [socket, setSocket] = useState<Socket | null>(null)
  const [connected, setConnected] = useState(false)

  useEffect(() => {
    if (!auth?.data?.access_token) {
      if (socket) {
        socket.disconnect()
        setSocket(null)
        setConnected(false)
      }
      return
    }

    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:30001'

    // Connect to the socket server
    const socketInstance = io(apiUrl, {
      transports: ['websocket'], // force WebSocket first
      autoConnect: true,
    })

    socketInstance.on('connect', () => {
      setConnected(true)
      console.log('Socket connected:', socketInstance.id)

      // Initialize socket session on backend
      socketInstance.emit('session-init', {
        token: auth.data.access_token,
        username: currentUser?.username,
        userId: currentUser?.id,
      })
    })

    socketInstance.on('disconnect', () => {
      setConnected(false)
      console.log('Socket disconnected')
    })

    setSocket(socketInstance)

    return () => {
      socketInstance.disconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.data?.access_token, currentUser?.id])

  return <SocketContext.Provider value={{socket, connected}}>{children}</SocketContext.Provider>
}
